import { useEffect, useId, useMemo, useRef, useState } from "react";
import { LayoutGrid, Loader2, LogOut, Search } from "lucide-react";
import { buildWorkspaceOptions, matchesWorkspaceQuery } from "@/components/workspaceOptions";
import { AgentPicture } from "@/components/AgentPicture";

function pluralize(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function WorkspacePicker({ memberships = [], selectClient, member = null, signOut = null }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showTestWorkspaces, setShowTestWorkspaces] = useState(false);
  const [pendingSlug, setPendingSlug] = useState("");
  const searchInputRef = useRef(null);
  const resultCountId = useId();

  const options = useMemo(() => buildWorkspaceOptions(memberships), [memberships]);
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const matches = options.filter((option) => matchesWorkspaceQuery(option, normalizedQuery));
  const brandOptions = matches.filter((option) => option.kind === "brand");
  const testOptions = matches.filter((option) => option.kind === "test");
  const testWorkspacesVisible = showTestWorkspaces || Boolean(normalizedQuery) || brandOptions.length === 0;
  const visibleOptions = testWorkspacesVisible ? [...brandOptions, ...testOptions] : brandOptions;
  const testGroupIndex = !normalizedQuery && testWorkspacesVisible && brandOptions.length > 0 && testOptions.length > 0
    ? brandOptions.length
    : -1;

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const resultCountText = normalizedQuery
    ? `${matches.length} of ${pluralize(options.length, "workspace")} match`
    : testOptions.length > 0 && !showTestWorkspaces && brandOptions.length > 0
      ? `${pluralize(brandOptions.length, "brand workspace")} · ${testOptions.length} test hidden`
      : pluralize(options.length, "workspace");

  const choose = (clientSlug) => {
    if (!clientSlug || pendingSlug) return;
    setPendingSlug(clientSlug);
    let result;
    try {
      result = selectClient(clientSlug);
    } catch {
      setPendingSlug("");
      return;
    }
    Promise.resolve(result).catch(() => setPendingSlug(""));
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter" && visibleOptions.length > 0) {
      event.preventDefault();
      choose(visibleOptions[0].clientSlug);
    }
  };

  return (
    <div className="mc-bg flex min-h-screen items-center justify-center px-4 py-10">
      <div data-testid="workspace-picker" className="glass-dark w-full max-w-lg rounded-2xl p-6 text-white sm:p-8">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sm-accent/15 text-sm-accent">
            <LayoutGrid size={20} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h1 className="font-display text-xl font-bold">Choose a brand workspace</h1>
            <p className="mt-1 text-sm leading-relaxed text-white/60">
              {member?.email ? <span className="block truncate">Signed in as {member.email}.</span> : null}
              Open a workspace to see its agent. You can switch brands any time from the sidebar.
            </p>
          </div>
        </div>

        <div className="relative mt-5">
          <Search size={14} aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            ref={searchInputRef}
            data-testid="workspace-picker-search"
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            aria-label="Search workspaces"
            aria-describedby={resultCountId}
            placeholder="Search by brand name"
            autoComplete="off"
            className="h-10 w-full rounded-lg border border-white/15 bg-white/[0.06] pl-9 pr-3 text-sm text-white outline-none placeholder:text-white/35 focus:border-sm-accent/60 focus:ring-2 focus:ring-sm-accent/25"
          />
        </div>
        <div
          id={resultCountId}
          data-testid="workspace-picker-result-count"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="mt-2 text-xs text-white/50"
        >
          {resultCountText}
        </div>

        <div data-testid="workspace-picker-list" className="mt-3 max-h-[52vh] space-y-1.5 overflow-y-auto pr-1">
          {visibleOptions.map((option, index) => (
            <div key={option.clientSlug}>
              {index === testGroupIndex ? (
                <div data-testid="workspace-picker-test-group" className="px-1 pb-1.5 pt-3 font-mono text-[10px] uppercase tracking-wider text-white/40">
                  Test &amp; sandbox
                </div>
              ) : null}
              <button
                type="button"
                data-testid={`workspace-pick-${option.clientSlug}`}
                disabled={Boolean(pendingSlug)}
                aria-busy={pendingSlug === option.clientSlug}
                onClick={() => choose(option.clientSlug)}
                className="flex w-full items-center gap-3 rounded-lg border border-white/15 bg-white/[0.05] px-3 py-2.5 text-left transition-colors hover:border-white/25 hover:bg-white/[0.1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent/70 disabled:cursor-progress disabled:opacity-60"
              >
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-display text-xs font-bold ${option.kind === "test" ? "bg-white/10 text-white/60" : "bg-sm-accent/15 text-sm-accent"}`}>
                  <AgentPicture src={option.agentImageUrl} fallback={option.initials} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">{option.clientName}</span>
                  <span className="mt-0.5 block truncate text-xs text-white/45">
                    {option.roleLabel}
                    {option.showSlug ? <span className="font-mono text-[10px] text-white/35"> · {option.clientSlug}</span> : null}
                  </span>
                </span>
                {pendingSlug === option.clientSlug ? <Loader2 size={14} aria-hidden="true" className="shrink-0 animate-spin text-sm-accent" /> : null}
              </button>
            </div>
          ))}
          {visibleOptions.length === 0 ? (
            <div data-testid="workspace-picker-empty" role="status" className="rounded-lg border border-dashed border-white/15 px-3 py-6 text-center text-sm text-white/50">
              No workspaces match “{searchQuery.trim()}”
            </div>
          ) : null}
        </div>

        {!normalizedQuery && testOptions.length > 0 && brandOptions.length > 0 ? (
          <button
            type="button"
            data-testid="workspace-picker-toggle-test"
            aria-expanded={showTestWorkspaces}
            onClick={() => setShowTestWorkspaces((previous) => !previous)}
            className="mt-3 text-xs text-white/55 underline-offset-2 hover:text-white hover:underline"
          >
            {showTestWorkspaces ? "Hide" : "Show"} {pluralize(testOptions.length, "test & sandbox workspace")}
          </button>
        ) : null}

        {signOut ? (
          <div className="mt-6 flex items-center justify-between gap-3 border-t border-white/10 pt-4 text-xs text-white/50">
            <span>Not the right account?</span>
            <button
              type="button"
              onClick={signOut}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1.5 font-semibold text-white/80 transition-colors hover:bg-white/10 hover:text-white"
            >
              <LogOut size={12} aria-hidden="true" />
              Sign out
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default WorkspacePicker;
